import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ReportJob } from '../models/models';
import api from './api';

interface ReportJobState {
  reportJobData: ReportJob[];
  total: number;
  totalPages: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedReportJob: ReportJob | null;
}

const initialState: ReportJobState = {
  reportJobData: [],
  total: 0,
  totalPages: 0,
  currentPage: 0,
  loading: false,
  error: null,
  successMessage: null,
  selectedReportJob: null,
};

export const fetchReportJobData = createAsyncThunk<
  { data: ReportJob[]; total: number; totalPages: number; currentPage: number },
  { limit: number; offset: number; departement_id?: number | null },
  { rejectValue: string }
>(
  'reportJob/fetchAll',
  async ({ limit, offset, departement_id }, thunkAPI) => {
    try {
      const response = await api.get('/reportJob', { params: { limit, offset, departement_id } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec du chargement.');
    }
  }
);

export const createReportJob = createAsyncThunk<
  { job_id: number },
  Omit<ReportJob, 'id' | 'statut' | 'file_path' | 'error_message'>,
  { rejectValue: string }
>(
  'reportJob/create',
  async (job, thunkAPI) => {
    try {
      const response = await api.post('/reportJob', job);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la création.');
    }
  }
);

export const pollReportJobStatus = createAsyncThunk<
  { id: number; statut: string; file_path: string | null; error_message: string | null },
  number,
  { rejectValue: string }
>(
  'reportJob/pollStatus',
  async (id, thunkAPI) => {
    try {
      const response = await api.get('/reportJob/status', { params: { id } });
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec.');
    }
  }
);

export const deleteReportJob = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>(
  'reportJob/delete',
  async (id, thunkAPI) => {
    try {
      await api.delete('/reportJob', { params: { id } });
      return id;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data?.error || error.response?.data?.message || 'Échec de la suppression.');
    }
  }
);

const reportJobSlice = createSlice({
  name: 'reportJob',
  initialState,
  reducers: {
    setSelectedReportJob: (state, action: PayloadAction<ReportJob | null>) => {
      state.selectedReportJob = action.payload;
    },
    clearReportJobData: (state) => {
      state.reportJobData = [];
    },
    clearMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportJobData.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchReportJobData.fulfilled, (state, action) => {
        state.loading = false;
        state.reportJobData = action.payload.data;
        state.total       = action.payload.total;
        state.totalPages  = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
      })
      .addCase(fetchReportJobData.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(createReportJob.pending,   (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(createReportJob.fulfilled, (state) => { state.loading = false; state.successMessage = 'Rapport en cours de génération...'; })
      .addCase(createReportJob.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; })

      .addCase(pollReportJobStatus.fulfilled, (state, action) => {
        const job = state.reportJobData.find(j => j.id === action.payload.id);
        if (job) {
          job.statut       = action.payload.statut as any;
          job.file_path    = action.payload.file_path;
          job.error_message = action.payload.error_message;
        }
      })

      .addCase(deleteReportJob.pending,   (state) => { state.loading = true; state.error = null; state.successMessage = null; })
      .addCase(deleteReportJob.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Job supprimé !';
        state.reportJobData = state.reportJobData.filter(j => j.id !== action.payload);
      })
      .addCase(deleteReportJob.rejected,  (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export const { setSelectedReportJob, clearReportJobData, clearMessages } = reportJobSlice.actions;
export default reportJobSlice.reducer;
